import { createAsyncThunk } from '@reduxjs/toolkit';
import OAuthService from 'src/app/auth/services/oAuthService';
import { STATUSES, toUiStatus, toApiStatus } from '../constants/statuses';
import { months, MONTH_MAP } from '../utils/budgetHierarchyUtils';
import { isSameAmount, buildLineKey } from '../utils/budgetValueUtils';
import { findBudgetViolations, buildViolationRejection } from '../utils/budgetValidation';
import { buildUrl, fetchLatestBudgets } from './budgetApi';
import { loadHistoryBudgets, loadHistorySales } from './historySourceCache';

const STALE_STATUS_MESSAGE = (latestStatus) =>
    `This territory was updated by someone else (status is now ${latestStatus.replace(/_/g, ' ')}). Please reload the page.`;

export const saveBudgetData = createAsyncThunk(
    'salesBudget/saveData',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState().salesBudget;
            const { budgetData, year, budgetType, activeTerritoryId, territoryStatuses, budgetDocumentIds, dirtyRowIds } = state;

            const dirtyIdSet = new Set((dirtyRowIds || []).map(String));

            const invalidRows = findBudgetViolations(budgetData, budgetType, activeTerritoryId, dirtyIdSet);
            if (invalidRows.length > 0) {
                return rejectWithValue(buildViolationRejection(invalidRows, budgetType));
            }
            const territoryProducts = budgetData.filter(row =>
                row.type === 'PRODUCT' &&
                String(row.territoryId) === String(activeTerritoryId) &&
                dirtyIdSet.has(String(row.id))
            );

            if (territoryProducts.length === 0) {
                const hasOtherDirty = budgetData.some(row =>
                    row.type === 'PRODUCT' && dirtyIdSet.has(String(row.id))
                );
                return rejectWithValue(hasOtherDirty
                    ? 'No changes in this territory. You have unsaved changes in another territory - open it and save.'
                    : 'No changes to save for this territory.');
            }

            let latest;
            try {
                latest = await fetchLatestBudgets(budgetType, year, activeTerritoryId);
            } catch (e) {
                return rejectWithValue('Could not check the latest saved data. Please try again.');
            }

            const localStatus = territoryStatuses[String(activeTerritoryId)] || territoryStatuses[activeTerritoryId] || STATUSES.NEW;
            const latestStatus = toUiStatus(latest.territoryDoc?.budgetDetails);
            if (latest.territoryDoc && latestStatus !== localStatus) {
                return rejectWithValue(STALE_STATUS_MESSAGE(latestStatus));
            }

            const flatBudgetsArray = [];
            const conflictRows = [];

            territoryProducts.forEach(prod => {
                const conflictMonths = [];

                months.forEach(monthKey => {
                    const currentValue = Number(prod[monthKey]) || 0;
                    const baseValue = Number(prod[`base_${monthKey}`]) || 0;
                    if (isSameAmount(currentValue, baseValue)) return;

                    const knownId = prod[`${monthKey}_id`];
                    const dbLine = (knownId && latest.linesById.get(String(knownId)))
                        || latest.linesByKey.get(buildLineKey(prod.companyId, prod.showroomId, prod.categoryId, prod.productId, MONTH_MAP[monthKey]))
                        || null;
                    const dbValue = dbLine ? Number(dbLine.budgetAmount) || 0 : 0;

                    if (!isSameAmount(dbValue, baseValue) && !isSameAmount(dbValue, currentValue)) {
                        conflictMonths.push(monthKey.toUpperCase());
                    }

                    flatBudgetsArray.push({
                        id: dbLine?.id || knownId || null,
                        customerNum: Number(prod.companyId) || null,
                        showroomNum: prod.showroomId ? Number(prod.showroomId) : null,
                        productNum: Number(prod.productId) || null,
                        territoryId: String(activeTerritoryId),
                        type: budgetType.toUpperCase(),
                        year: Number(year),
                        orderCategory: prod.categoryId,
                        month: MONTH_MAP[monthKey],
                        budgetAmount: currentValue
                    });
                });

                if (conflictMonths.length > 0) {
                    conflictRows.push({
                        id: String(prod.id),
                        name: prod.name,
                        hierarchy: prod.hierarchy?.join(' > '),
                        months: conflictMonths,
                    });
                }
            });

            if (conflictRows.length > 0) {
                return rejectWithValue({
                    message: 'Someone else changed these cells after you opened the page. Please reload to see the latest values, then enter your changes again.',
                    invalidRowIds: conflictRows.map(r => r.id),
                    invalidRows: conflictRows,
                });
            }

            if (flatBudgetsArray.length === 0) {
                return [];
            }

            const apiStatus = toApiStatus(localStatus);

            const payload = [
                {
                    budgetDetails: {
                        id: budgetDocumentIds[String(activeTerritoryId)] || latest.territoryDoc?.budgetDetails?.id || null,
                        territoryId: String(activeTerritoryId),
                        year: Number(year),
                        type: budgetType.toUpperCase(),
                        status: apiStatus.status,
                        partDiscDone: apiStatus.partDiscDone
                    },
                    budgets: flatBudgetsArray
                }
            ];

            const response = await OAuthService.addData(buildUrl('/api/salesBudget/v2/save-sales-budgets'), payload);

            return response.data;
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);
const SNAPSHOT_STATUSES = [STATUSES.PENDING_APPROVAL, STATUSES.PENDING_CONFIRMATION];

const buildSnapshotLines = (territoryDoc, territoryId, year, budgetType) =>
    (territoryDoc?.budgets || []).map(b => ({
        id: b.id,
        customerNum: b.customerNum,
        showroomNum: b.showroomNum,
        productNum: b.productNum,
        territoryId: String(territoryId),
        type: budgetType.toUpperCase(),
        year: Number(year),
        orderCategory: b.orderCategory,
        month: b.month,
        budgetAmount: b.budgetAmount
    }));

export const changeBudgetStatus = createAsyncThunk(
    'salesBudget/changeStatus',
    async ({ territoryId, newStatus, comment }, { getState, rejectWithValue }) => {
        try {
            const state = getState().salesBudget;
            const { budgetData, year, budgetType, budgetDocumentIds, territoryStatuses } = state;

            if (SNAPSHOT_STATUSES.includes(newStatus)) {
                const invalidRows = findBudgetViolations(budgetData, budgetType, territoryId);
                if (invalidRows.length > 0) {
                    return rejectWithValue(buildViolationRejection(invalidRows, budgetType));
                }
            }

            const ENDPOINT_BY_STATUS = {
                [STATUSES.PENDING_APPROVAL]: 'api/salesBudget/v2/submit-sales-budgets',
                [STATUSES.PENDING_CONFIRMATION]: 'api/salesBudget/v2/send-sales-budgets-for-confirmation',
                [STATUSES.APPROVED]: 'api/salesBudget/v2/approve-sales-budgets',
                [STATUSES.CONFIRMED]: 'api/salesBudget/v2/confirm-budgets',
                [STATUSES.REJECTED]: 'api/salesBudget/v2/reject-sales-budgets',
                [STATUSES.PARTNER_DISCUSSION_DONE]: 'api/salesBudget/v2/partner-discussion-done',
            };
            const endpoint = ENDPOINT_BY_STATUS[newStatus] || '';

            if (!endpoint) return rejectWithValue("Invalid status action.");

            let latest;
            try {
                latest = await fetchLatestBudgets(budgetType, year, territoryId);
            } catch (e) {
                return rejectWithValue('Could not check the latest saved data. Please try again.');
            }

            const localStatus = territoryStatuses[String(territoryId)] || territoryStatuses[territoryId] || STATUSES.NEW;
            const latestStatus = toUiStatus(latest.territoryDoc?.budgetDetails);
            if (latestStatus !== localStatus) {
                return rejectWithValue(STALE_STATUS_MESSAGE(latestStatus));
            }

            const currentStatus = toApiStatus(localStatus).status;

            let finalPayload;

            if (newStatus === STATUSES.PARTNER_DISCUSSION_DONE) {
                const docId = budgetDocumentIds[String(territoryId)];
                if (!docId) return rejectWithValue("Cannot mark discussion done: Document ID is missing.");

                finalPayload = [docId];
            } else {
                finalPayload = [
                    {
                        budgetDetails: {
                            id: budgetDocumentIds[String(territoryId)] || null,
                            territoryId: String(territoryId),
                            year: Number(year),
                            type: budgetType.toUpperCase(),
                            status: currentStatus,
                            comments: newStatus === STATUSES.REJECTED ? (comment || '') : (latest.territoryDoc?.budgetDetails?.comments || ''),
                            partDiscDone: newStatus === STATUSES.PARTNER_DISCUSSION_DONE ? true : null
                        },
                        budgets: SNAPSHOT_STATUSES.includes(newStatus) ? buildSnapshotLines(latest.territoryDoc, territoryId, year, budgetType) : []
                    }
                ];
            }

            await OAuthService.addData(buildUrl(endpoint), finalPayload);

            let confirmedStatus = newStatus;
            try {
                const saved = await fetchLatestBudgets(budgetType, year, territoryId);
                confirmedStatus = toUiStatus(saved.territoryDoc?.budgetDetails) || newStatus;
            } catch (e) {
                // Could not re-confirm the saved status from the server; fall back to the requested status.
            }

            return { territoryId, newStatus: confirmedStatus, comment };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchVersionHistory = createAsyncThunk(
    'salesBudget/fetchVersionHistory',
    async (budgetsId, { rejectWithValue }) => {
        try {
            const url = buildUrl(`api/salesBudget/v2/get-budget-versioninfo`, { budgetsId });
            const response = await OAuthService.getList(url);
            return response.data || [];
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const restoreBudgetVersion = createAsyncThunk(
    'salesBudget/restoreBudgetVersion',
    async ({ territoryId, year, type, versionNo }, { getState, rejectWithValue }) => {
        try {
            const url = buildUrl(`api/salesBudget/v2/get-budget-version`, {
                year,
                type,
                territoryId,
                versionNo
            });
            const response = await OAuthService.getList(url);
            const list = Array.isArray(response.data) ? response.data : (response.data ? [response.data] : []);
            const versionBudgets = list.flatMap(item => (Array.isArray(item?.budgets) ? item.budgets : []));

            if (versionBudgets.length === 0) {
                return rejectWithValue(`Version ${versionNo} has no saved budget data. Nothing was restored.`);
            }

            const versionValues = new Map();
            versionBudgets.forEach(b => {
                if (!b.month) return;
                const key = buildLineKey(b.customerNum, b.showroomNum, b.orderCategory, b.productNum, 'ALL');
                const monthKey = String(b.month).trim().substring(0, 3).toLowerCase();
                if (!months.includes(monthKey)) return;
                if (!versionValues.has(key)) versionValues.set(key, {});
                const vals = versionValues.get(key);
                vals[monthKey] = (vals[monthKey] || 0) + (Number(b.budgetAmount) || 0);
            });

            const { budgetData } = getState().salesBudget;
            const restoredRows = {};
            budgetData.forEach(row => {
                if (row.type !== 'PRODUCT' || String(row.territoryId) !== String(territoryId)) return;
                const key = buildLineKey(row.companyId, row.showroomId, row.categoryId, row.productId, 'ALL');
                const vals = versionValues.get(key) || {};
                const newValues = {};
                let changed = false;
                months.forEach(m => {
                    newValues[m] = Number(vals[m]) || 0;
                    if (!isSameAmount(newValues[m], row[m])) changed = true;
                });
                if (changed) restoredRows[String(row.id)] = newValues;
            });

            return { territoryId, versionNo, restoredRows, changedCount: Object.keys(restoredRows).length };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchEurostatData = createAsyncThunk(
    'salesBudget/fetchEurostatData',
    async (year, { rejectWithValue }) => {
        try {
            const url = buildUrl('api/euroStat/getEurostat', { year });
            const response = await OAuthService.getList(url);
            return { year, data: response.data };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);

export const fetchRowHistory = createAsyncThunk(
    'salesBudget/fetchRowHistory',
    async ({ rowId, territoryId, customerNum, validCustomerNums, showroomId, categoryId, productId, years }, { getState, rejectWithValue }) => {
        try {
            const state = getState().salesBudget;
            const currentBudgetType = state.budgetType || 'INTERNAL';

            const cacheKey = `${rowId}__${currentBudgetType}`;
            const existingHistory = state.historyData[cacheKey] || {};
            const yearsToFetch = years.filter(year => !existingHistory[year]);

            if (yearsToFetch.length === 0) return { rowId: cacheKey, newData: {} };

            const newData = {};

            const getMonthKey = (m) => {
                if (!m) return null;
                if (!isNaN(m)) return months[Number(m) - 1] || null;
                return String(m).substring(0, 3).toLowerCase();
            };

            await Promise.all(yearsToFetch.map(async (year) => {
                try {
                    const [budgetArray, salesArray] = await Promise.all([
                        loadHistoryBudgets(year, territoryId, currentBudgetType),
                        loadHistorySales(year, territoryId)
                    ]);

                    const yearData = {
                        total: { budget: 0, sales: 0 },
                        jan: { budget: 0, sales: 0 }, feb: { budget: 0, sales: 0 }, mar: { budget: 0, sales: 0 },
                        apr: { budget: 0, sales: 0 }, may: { budget: 0, sales: 0 }, jun: { budget: 0, sales: 0 },
                        jul: { budget: 0, sales: 0 }, aug: { budget: 0, sales: 0 }, sep: { budget: 0, sales: 0 },
                        oct: { budget: 0, sales: 0 }, nov: { budget: 0, sales: 0 }, dec: { budget: 0, sales: 0 },
                        hasConfirmedBudget: budgetArray.length > 0
                    };

                    budgetArray.forEach(b => {
                        let match = true;
                        if (territoryId && String(b.territoryId) !== String(territoryId)) match = false;
                        if (validCustomerNums && validCustomerNums.length > 0) {
                            if (!validCustomerNums.includes(String(b.customerNum))) match = false;
                        } else if (customerNum && String(b.customerNum) !== String(customerNum)) {
                            match = false;
                        }

                        if (showroomId && String(b.showroomNum) !== String(showroomId)) match = false;
                        if (categoryId) {
                            const validCats = String(categoryId).split(',');
                            if (!validCats.includes(b.orderCategory)) match = false;
                        }
                        if (productId && String(b.productNum) !== String(productId)) match = false;

                        if (match) {
                            const amt = Number(b.budgetAmount) || 0;
                            const mKey = getMonthKey(b.month);
                            yearData.total.budget += amt;
                            if (mKey && yearData[mKey]) yearData[mKey].budget += amt;
                        }
                    });

                    salesArray.forEach(s => {
                        let match = true;
                        if (validCustomerNums && validCustomerNums.length > 0) {
                            if (!validCustomerNums.includes(String(s.customerNum))) match = false;
                        } else if (customerNum && String(s.customerNum) !== String(customerNum)) {
                            match = false;
                        }

                        if (showroomId && String(s.showroomNum) !== String(showroomId)) match = false;
                        if (categoryId) {
                            const validCats = String(categoryId).split(',');
                            if (!validCats.includes(s.category)) match = false;
                        }
                        if (productId && String(s.vendorNum) !== String(productId)) match = false;

                        if (match) {
                            const amt = Number(s.total) || 0;
                            const mKey = getMonthKey(s.month);
                            yearData.total.sales += amt;
                            if (mKey && yearData[mKey]) yearData[mKey].sales += amt;
                        }
                    });

                    newData[year] = yearData;
                } catch (err) {
                    newData[year] = { total: { budget: 0, sales: 0 }, hasConfirmedBudget: false };
                }
            }));

            return { rowId: cacheKey, newData };
        } catch (error) {
            return rejectWithValue(error.message);
        }
    }
);
